import React, { useState, useEffect, useCallback } from 'react';
import { getProductVariants, getProductVariantsByTypeId, deleteProductVariant, patchProductVariant, getFurnitureTypes, logError } from '../api';
import GenericList from './Global/GenericList';
import ProductVariantModal from './ProductVariantModal';
import DisabledToggle from './ui/DisabledToggle';
import DisabledRowStyles from './ui/DisabledRowStyles';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import useFilterPersistence from '../hooks/useFilterPersistence';
import { Form, Row, Col, Button, Container } from 'react-bootstrap';

const ProductVariantList = () => {
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingVariant, setEditingVariant] = useState(null);
    const [furnitureTypes, setFurnitureTypes] = useState([]);
    const [selectedTypeId, setSelectedTypeId] = useFilterPersistence('filters_product_variants', '');
    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const { orderBy, orderDesc, handleSort } = useSorting();

    const loadVariants = useCallback(async () => {
        try {
            setLoading(true);
            const data = selectedTypeId 
                ? await getProductVariantsByTypeId(selectedTypeId, includeDisabled, orderBy, orderDesc)
                : await getProductVariants(includeDisabled, orderBy, orderDesc);
            setVariants(data);
        } catch (error) {
            logError(error);
            console.error('Error loading product variants:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedTypeId, includeDisabled, orderBy, orderDesc]);

    useEffect(() => {
        loadFurnitureTypes();
    }, []);

    useEffect(() => {
        loadVariants();
    }, [loadVariants]);

    const loadFurnitureTypes = async () => {
        try {
            const types = await getFurnitureTypes();
            setFurnitureTypes(types);
        } catch (error) {
            logError(error);
            console.error('Error loading furniture types:', error);
        }
    };

    const handleToggleStatus = async (variantId, currentStatus) => {
        try {
            await patchProductVariant(variantId, { id: variantId, disabled: !currentStatus });
            setVariants((prevVariants) => 
                prevVariants.map((variant) => 
                    variant.id === variantId 
                        ? { ...variant, disabled: !currentStatus }
                        : variant
                )
            );
        } catch (error) {
            logError(error);
            console.error('Error toggling variant status:', error);
        }
    };

    const handleDelete = async (variant) => {
        try {
            await deleteProductVariant(variant.id);
            setVariants(prev => prev.filter(v => v.id !== variant.id));
        } catch (error) {
            logError(error);
            console.error('Error deleting product variant:', error);
        }
    };

    const handleEdit = (variant) => {
        setEditingVariant(variant);
        setShowModal(true);
    };

    const handleCreate = () => {
        setEditingVariant(null);
        setShowModal(true);
    };

    const handleModalSave = () => {
        loadVariants();
        setShowModal(false);
        setEditingVariant(null);
    };

    const columns = [
        { key: 'id', header: 'ID', sortKey: 'id' },
        { key: 'type.typeName', header: 'Tipo', sortKey: 'type.typeName' },
        { key: 'name', header: 'Nombre', sortKey: 'name' },
        { 
            key: 'priceDelta', 
            header: 'Precio añadido',
            type: 'currency',
            sortKey: 'priceDelta'
        }
    ];



    return (
        <>
            <DisabledRowStyles />
            <Container fluid className="my-4">
                <Row className="mb-4">
                    <Col xs={12} md={6}>
                        <h1 data-testid="page-title">Variantes de Producto</h1>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
                        <DisabledToggle 
                            includeDisabled={includeDisabled}
                            onChange={setIncludeDisabled}
                        />
                        <Button variant="primary" onClick={handleCreate} data-testid="new-variant-button">
                            Nueva Variante
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={4}>
                        <Form.Group>
                            <Form.Label data-testid="type-filter-label">Filtrar por Tipo</Form.Label>
                            <Form.Select
                                value={selectedTypeId}
                                onChange={(e) => setSelectedTypeId(e.target.value)}
                                data-testid="type-filter-select"
                            >
                                <option value="">Todos los tipos</option>
                                {furnitureTypes.map(type => (
                                    <option 
                                        key={type.id} 
                                        value={type.id}
                                        style={{ fontWeight: type.isFavorite ? 'bold' : 'normal' }}
                                    >
                                        {type.typeName}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            
                <GenericList
                    data={variants}
                    columns={columns}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                    deleteMessage={(item) => `¿Estás seguro de que deseas eliminar la variante "${item?.name}"?`}
                    emptyMessage="No se encontraron variantes de producto."
                    containerFluid={false}
                    sortable={true}
                    onSort={handleSort}
                    testIdPrefix="variant"
                />
            
                <ProductVariantModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onSave={handleModalSave}
                    variant={editingVariant}
                />
            </Container>
        </>
    );
};

export default ProductVariantList;